import { isAdmin as isAdminRole } from '../../../utils/userRoles'

/**
 * Checks if a user's email is verified based on email_verified field,
 * with fallback to legacy verified field.
 * @param {Object} userRow - User object
 * @returns {boolean} True if email is verified
 */
export function isEmailVerified(userRow) {
  if (typeof userRow?.email_verified === 'boolean')
    return userRow.email_verified
  return !!userRow?.verified
}

/**
 * Formats a user's full name from firstname and lastname fields.
 * Returns em-dash (—) if both names are empty.
 * @param {Object} userRow - User object with firstname and lastname
 * @returns {string} Formatted full name or em-dash
 */
export function formatUserName(userRow) {
  const first = (userRow?.firstname || '').trim()
  const last = (userRow?.lastname || '').trim()
  const full = `${first} ${last}`.trim()
  return full || '—'
}

/**
 * Generates a random temporary password using allowed characters.
 * @param {number} [length=12] - Length of password to generate
 * @returns {string} Generated password
 */
export function generateTempPassword(length = 12) {
  const chars =
    'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*'
  let pwd = ''
  for (let i = 0; i < length; i++) {
    pwd += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return pwd
}

/**
 * Filters and sorts users based on search criteria and sort preferences.
 * Automatically filters out disabled/deleted users.
 * @param {Object} params - Filter and sort parameters
 * @param {Array} params.users - Array of user objects
 * @param {string} params.searchTerm - Search query string
 * @param {string} params.sortColumn - Column to sort by (id, email, name, user_type, verified, created_at, registration_count, active_league_count)
 * @param {string} params.sortDirection - Sort direction ('asc' or 'desc')
 * @param {string} params.activeFilter - Filter type ('all', 'never_registered', 'not_in_active_league')
 * @returns {Array} Filtered and sorted user array
 */
export function getFilteredAndSortedUsers({
  users,
  searchTerm,
  sortColumn,
  sortDirection,
  activeFilter,
}) {
  let filtered = users.filter((userRow) => !userRow.disabled_or_deleted)

  if (activeFilter === 'never_registered') {
    filtered = filtered.filter((userRow) => userRow.registration_count === 0)
  } else if (activeFilter === 'not_in_active_league') {
    filtered = filtered.filter((userRow) => userRow.active_league_count === 0)
  }

  if (searchTerm) {
    const term = searchTerm.toLowerCase()
    filtered = filtered.filter((userRow) => {
      const name = formatUserName(userRow).toLowerCase()
      const email = (userRow.email || '').toLowerCase()
      const userType = (userRow.user_type || '').toLowerCase()
      return (
        email.includes(term) ||
        name.includes(term) ||
        userType.includes(term) ||
        String(userRow.id).includes(term)
      )
    })
  }

  return [...filtered].sort((a, b) => {
    let aVal
    let bVal

    switch (sortColumn) {
      case 'id':
        aVal = a.id
        bVal = b.id
        break
      case 'email':
        aVal = (a.email || '').toLowerCase()
        bVal = (b.email || '').toLowerCase()
        break
      case 'name':
        aVal = formatUserName(a).toLowerCase()
        bVal = formatUserName(b).toLowerCase()
        break
      case 'user_type':
        aVal = (a.user_type || '').toLowerCase()
        bVal = (b.user_type || '').toLowerCase()
        break
      case 'verified':
        aVal = isEmailVerified(a) ? 1 : 0
        bVal = isEmailVerified(b) ? 1 : 0
        break
      case 'created_at':
        aVal = a.created_at ? new Date(a.created_at).getTime() : 0
        bVal = b.created_at ? new Date(b.created_at).getTime() : 0
        break
      case 'registration_count':
        aVal = a.registration_count || 0
        bVal = b.registration_count || 0
        break
      case 'active_league_count':
        aVal = a.active_league_count || 0
        bVal = b.active_league_count || 0
        break
      default:
        return 0
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
    return 0
  })
}

/**
 * Calculates statistics for admin users display.
 * Filters out disabled/deleted users from stats.
 * @param {Array} users - Array of all user objects
 * @param {number} showingCount - Number of users currently displayed after filtering
 * @returns {Object} Statistics object with total, verified, admins, neverRegistered, notInActiveLeague, showing counts
 */
export function getAdminUsersStats(users, showingCount) {
  const activeUsers = users.filter((u) => !u.disabled_or_deleted)
  const verifiedCount = activeUsers.filter(isEmailVerified).length
  const adminCount = activeUsers.filter((u) => isAdminRole(u)).length
  const neverRegistered = activeUsers.filter(
    (u) => u.registration_count === 0,
  ).length
  const notInActiveLeague = activeUsers.filter(
    (u) => u.active_league_count === 0,
  ).length

  return {
    total: activeUsers.length,
    verified: verifiedCount,
    admins: adminCount,
    neverRegistered,
    notInActiveLeague,
    showing: showingCount,
  }
}

/**
 * Validates user edit form data and returns validation errors.
 * @param {Object} editFormData - Form data to validate
 * @param {string} editFormData.email - User email
 * @param {string} editFormData.firstname - User first name
 * @param {string} editFormData.lastname - User last name
 * @param {string} editFormData.user_type - User type (player, admin, owner)
 * @returns {Object} Object with field names as keys and error messages as values
 */
export function validateEditFormData(editFormData) {
  const errors = {}

  if (!editFormData.email || !editFormData.email.trim()) {
    errors.email = 'Email is required'
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(editFormData.email)) {
      errors.email = 'Invalid email format'
    }
  }

  if (!editFormData.firstname || !editFormData.firstname.trim()) {
    errors.firstname = 'First name is required'
  }
  if (!editFormData.lastname || !editFormData.lastname.trim()) {
    errors.lastname = 'Last name is required'
  }

  if (!['player', 'admin', 'owner'].includes(editFormData.user_type)) {
    errors.user_type = 'Invalid user type'
  }

  return errors
}

/**
 * Compares edit form data with original user data and returns only changed fields.
 * @param {Object} editFormData - New form data
 * @param {Object} userRow - Original user object
 * @returns {Object} Object containing only the fields that have changed
 */
export function getUserEditUpdates(editFormData, userRow) {
  const updates = {}

  if (editFormData.email !== userRow.email) {
    updates.email = editFormData.email
  }
  if (editFormData.firstname !== userRow.firstname) {
    updates.firstname = editFormData.firstname
  }
  if (editFormData.lastname !== userRow.lastname) {
    updates.lastname = editFormData.lastname
  }
  if (editFormData.user_type !== userRow.user_type) {
    updates.user_type = editFormData.user_type
  }
  if (editFormData.email_verified !== isEmailVerified(userRow)) {
    updates.email_verified = editFormData.email_verified
  }

  return updates
}
